import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../theme/ThemeContext';
import { getModalTheme } from '../theme/themeStyles';

export type InfoPopupAccent = 'amber' | 'orange' | 'emerald' | 'blue' | 'sky' | 'red' | 'default';

type InfoPopupDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  accent?: InfoPopupAccent;
};

function accentIconClass(accent: InfoPopupAccent, isDark: boolean): string {
  if (accent === 'red') {
    return isDark ? 'bg-red-950/50 text-red-400' : 'bg-red-50 text-red-700';
  }
  return isDark
    ? 'bg-[rgba(52,211,153,.16)] text-[#34d399]'
    : 'bg-[rgba(0,133,64,.12)] text-[#008540]';
}

export default function InfoPopupDialog({
  open,
  onClose,
  title,
  children,
  accent = 'default',
}: InfoPopupDialogProps) {
  const { isDark } = useTheme();
  const md = getModalTheme(isDark);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const dialog = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-popup-title"
    >
      <button
        type="button"
        className={`absolute inset-0 ${md.overlay} backdrop-blur-[2px]`}
        onClick={onClose}
        aria-label="Close"
      />
      <div className={`relative z-10 w-full max-w-md max-h-[min(85vh,520px)] flex flex-col ${md.dialog}`}>
        <div
          className={`flex items-start gap-3 border-b px-4 py-3.5 ${
            isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'
          }`}
        >
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${accentIconClass(accent, isDark)}`}
            aria-hidden
          >
            <i className="ri-information-line text-xl" />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3
              id="info-popup-title"
              className={`text-base font-semibold leading-snug ${isDark ? 'text-[#e6edf3]' : 'text-[#111827]'}`}
            >
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`shrink-0 rounded-lg p-1.5 transition-colors ${
              isDark
                ? 'text-[#8b949e] hover:bg-white/[0.06] hover:text-[#e6edf3]'
                : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#008540]'
            }`}
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div
          className={`overflow-y-auto px-4 py-3.5 text-sm leading-relaxed ${
            isDark ? 'text-[#c9d1d9]' : 'text-[#374151]'
          }`}
        >
          {children}
        </div>
        <div className={`border-t px-4 py-3 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`w-full rounded-[10px] py-2.5 text-sm font-semibold transition-colors ${
              isDark
                ? 'bg-[#008540] text-white hover:bg-[#097a34]'
                : 'bg-[#008540] text-white hover:opacity-90'
            }`}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  // The fields list is a higher stacking layer than the analysis column.
  // Portaling keeps this card centered on the screen instead of sliding under that list.
  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}
